import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { customersApi, type Customer } from "@/features/customers/api";
import { money, onDay, perMinute } from "@/features/customers/format";
import { MinutesBar } from "@/features/customers/minutes";

/** The dialogs that act on one existing customer.
 *
 * Shared by the Customers list and a single customer's page — the same Assign,
 * Add minutes, Reset password and Delete, so the two can never drift apart or
 * offer different wording for the same act. The Add-customer dialog stays with
 * the list: it belongs to the list, not to a customer.
 */

export const CUSTOMERS_KEY = ["customers"] as const;

export function errText(e: unknown): string {
  const detail = (e as { response?: { data?: { detail?: string; error?: { message?: string } } } })
    ?.response?.data;
  return detail?.detail ?? detail?.error?.message ?? "Something went wrong.";
}

export function AssignDialog({
  customer,
  onClose,
  onChanged,
}: {
  customer: Customer;
  onClose: () => void;
  onChanged: () => void;
}) {
  const q = useQuery({
    queryKey: [...CUSTOMERS_KEY, "assignable", customer.id],
    queryFn: () => customersApi.assignable(customer.id),
  });
  const [assistants, setAssistants] = useState<Set<string>>(
    () => new Set(customer.assistants.map((a) => a.id)),
  );
  const [numbers, setNumbers] = useState<Set<string>>(
    () => new Set(customer.phone_numbers.map((n) => n.id)),
  );

  const save = useMutation({
    mutationFn: () =>
      customersApi.assign(customer.id, {
        assistant_ids: [...assistants],
        phone_number_ids: [...numbers],
      }),
    onSuccess: () => {
      toast.success("Assignment saved.");
      onChanged();
      onClose();
    },
    onError: (e) => toast.error(errText(e)),
  });

  const toggleIn = (set: Set<string>, id: string, setter: (s: Set<string>) => void) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setter(next);
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Assign to {customer.name}</DialogTitle>
          <DialogDescription>
            Pick which of your assistants and phone numbers this customer owns. They
            see exactly these, plus anything they build themselves.
          </DialogDescription>
        </DialogHeader>
        {q.isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : (
          <div className="space-y-4">
            <div>
              <p className="mb-2 text-sm font-medium">Assistants</p>
              {(q.data?.assistants.length ?? 0) === 0 ? (
                <p className="text-sm text-muted-foreground">No unassigned assistants available.</p>
              ) : (
                <div className="max-h-40 space-y-2 overflow-y-auto">
                  {q.data!.assistants.map((a) => (
                    <label key={a.id} className="flex items-center gap-2 text-sm">
                      <Checkbox
                        checked={assistants.has(a.id)}
                        onCheckedChange={() => toggleIn(assistants, a.id, setAssistants)}
                      />
                      {a.name}
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div>
              <p className="mb-2 text-sm font-medium">Phone numbers</p>
              {(q.data?.phone_numbers.length ?? 0) === 0 ? (
                <p className="text-sm text-muted-foreground">No unassigned numbers available.</p>
              ) : (
                <div className="max-h-40 space-y-2 overflow-y-auto">
                  {q.data!.phone_numbers.map((n) => (
                    <label key={n.id} className="flex items-center gap-2 text-sm">
                      <Checkbox
                        checked={numbers.has(n.id)}
                        onCheckedChange={() => toggleIn(numbers, n.id, setNumbers)}
                      />
                      {n.number}
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? "Saving…" : "Save assignment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Minutes on top of the plan — the only way a customer gets more before their
 *  period resets, until they can buy them directly.
 *
 *  It shows what the tenant's own per-minute cost will be if the minutes are
 *  used, because that is the part granting them quietly spends. */
export function AddMinutesDialog({
  customer,
  onClose,
  onChanged,
}: {
  customer: Customer;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [minutes, setMinutes] = useState("");
  const [note, setNote] = useState("");
  const qc = useQueryClient();
  const previewQ = useQuery({
    queryKey: [...CUSTOMERS_KEY, "billing-preview"],
    queryFn: customersApi.billingPreview,
  });
  const n = Number(minutes);
  const valid = Number.isFinite(n) && Number.isInteger(n) && n !== 0;
  const costCents = valid && previewQ.data ? n * previewQ.data.per_minute_cents : 0;

  const grantsQ = useQuery({
    queryKey: [...CUSTOMERS_KEY, "grants", customer.id],
    queryFn: () => customersApi.minuteGrants(customer.id),
  });

  const save = useMutation({
    mutationFn: () => customersApi.addMinutes(customer.id, n, note),
    onSuccess: () => {
      toast.success(
        n > 0
          ? `${n.toLocaleString()} minutes added for ${customer.name}.`
          : `${Math.abs(n).toLocaleString()} minutes taken back.`,
      );
      qc.invalidateQueries({ queryKey: [...CUSTOMERS_KEY, "grants", customer.id] });
      onChanged();
      onClose();
    },
    onError: (e) => toast.error(errText(e)),
  });

  const u = customer.usage;
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add minutes — {customer.name}</DialogTitle>
          <DialogDescription>
            On top of their plan. Added minutes carry over between periods, and
            their calls start working again as soon as they have some.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1.5 rounded-lg border bg-muted/40 p-3 text-sm">
          <div className="flex items-baseline justify-between gap-4">
            <span className="text-muted-foreground">Used this period</span>
            <span className="font-medium tabular-nums">
              {u.minutes_used.toLocaleString()} of{" "}
              {u.minutes_allowed.toLocaleString()} min
            </span>
          </div>
          <MinutesBar usage={u} />
          {u.blocked && (
            <p className="pt-1 text-xs text-destructive">
              Their calls are being refused right now.
            </p>
          )}
        </div>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="add-min">Minutes</Label>
            <Input
              id="add-min"
              type="number"
              inputMode="numeric"
              placeholder="e.g. 500"
              value={minutes}
              onChange={(e) => setMinutes(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              A negative number takes minutes back — it is recorded either way.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="add-note">Reason (shown to the customer)</Label>
            <Input
              id="add-note"
              placeholder="e.g. paid by invoice"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
          {valid && n > 0 && previewQ.data?.configured && (
            <p className="text-xs text-muted-foreground">
              If they use all {n.toLocaleString()}, it costs you{" "}
              <span className="font-medium text-foreground">{money(costCents)}</span>{" "}
              from your balance at {perMinute(previewQ.data.per_minute_cents)}/min.
              Charge them for it yourself.
            </p>
          )}
        </div>

        {(grantsQ.data?.length ?? 0) > 0 && (
          <div className="space-y-1 border-t pt-3">
            <p className="text-xs font-medium text-muted-foreground">Already added</p>
            <ul className="max-h-32 space-y-1 overflow-y-auto text-xs">
              {grantsQ.data!.map((g) => (
                <li key={g.id} className="flex items-baseline justify-between gap-3">
                  <span className="font-medium tabular-nums">
                    {g.minutes > 0 ? "+" : ""}
                    {g.minutes.toLocaleString()} min
                  </span>
                  <span className="flex-1 truncate text-muted-foreground">
                    {g.note || "—"}
                  </span>
                  <span className="shrink-0 text-muted-foreground">
                    {onDay(g.created_at)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => save.mutate()} disabled={!valid || save.isPending}>
            {save.isPending
              ? "Saving…"
              : valid && n < 0
                ? `Take back ${Math.abs(n).toLocaleString()} min`
                : `Add ${valid ? n.toLocaleString() : ""} minutes`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ResetPasswordDialog({ customer, onClose }: { customer: Customer; onClose: () => void }) {
  const [password, setPassword] = useState("");
  const reset = useMutation({
    mutationFn: () => customersApi.resetPassword(customer.id, password),
    onSuccess: () => {
      toast.success("Password updated.");
      onClose();
    },
    onError: (e) => toast.error(errText(e)),
  });

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reset password — {customer.name}</DialogTitle>
          <DialogDescription>Set a new portal password for {customer.email}.</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="new-pw">New password</Label>
          <Input id="new-pw" type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => reset.mutate()} disabled={!password || reset.isPending}>
            {reset.isPending ? "Saving…" : "Set password"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DeleteDialog({
  customer,
  onClose,
  onDeleted,
}: {
  customer: Customer;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const del = useMutation({
    mutationFn: () => customersApi.remove(customer.id),
    onSuccess: () => {
      toast.success("Customer deleted. Their assistants and numbers returned to your pool.");
      onDeleted();
      onClose();
    },
    onError: (e) => toast.error(errText(e)),
  });

  return (
    <AlertDialog open onOpenChange={(v) => !v && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete {customer.name}?</AlertDialogTitle>
          <AlertDialogDescription>
            Their login is disabled and their assigned assistants and numbers return
            to your pool. Call history is kept.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={() => del.mutate()}
          >
            Delete customer
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
